"use client";

import { useCallback } from "react";
import { useSWRConfig } from "swr";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeItems } from "@/lib/hooks/useRealtimeItems";
import { shouldRevalidateNotificationCacheKey } from "@/lib/notifications/cache";

/**
 * Single Supabase Realtime subscription per signed-in user for `user_notifications`.
 * Keeps header badges, inbox page, and dropdown SWR caches aligned without duplicate channels.
 */
export function NotificationUserRealtimeSubscriber() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { mutate } = useSWRConfig();
  const userId = user?.id ?? "";
  const enabled = Boolean(isAuthenticated && !isLoading && userId);

  const invalidateCaches = useCallback(() => {
    void mutate(
      (key) => shouldRevalidateNotificationCacheKey(key, userId),
      undefined,
      { revalidate: true },
    );
  }, [mutate, userId]);

  useRealtimeItems({
    table: "user_notifications",
    filter: userId ? `user_id=eq.${userId}` : undefined,
    enabled,
    channelName: `notifications-user-realtime:${userId}`,
    onInsert: invalidateCaches,
    onUpdate: invalidateCaches,
    onDelete: invalidateCaches,
  });

  return null;
}
